import React, { useEffect } from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  useLocation,
  Outlet,
  Navigate,
} from "react-router-dom";
import Header from "./components/Header";
import Footer from "./components/Footer";
import Home from "./pages/Home";
import About from "./pages/About";
import Features from "./pages/Features";
import Pricing from "./pages/Pricing";
import Contact from "./pages/Contact";
import Blog from "./pages/Blog";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsOfUse from "./pages/TermsOfUse";
import Disclaimer from "./pages/Disclaimer";
import HowItWorks from "./pages/HowItWorks";
import UseCases from "./pages/UseCases";
import FAQ from "./pages/FAQ";
import Support from "./pages/Support";
import RefundPolicy from "./pages/RefundPolicy";
import { trackPageView } from "./utils/analytics";
import { AuthProvider } from "./context/AuthContext";
import { PlatformContextProvider } from "./context/PlatformContext";
import { ClientsProvider } from "./context/ClientsContext";
import { PropertiesProvider } from "./context/PropertiesContext";
import { DashboardProvider } from "./context/DashboardContext";
import ProtectedRoute from "./components/ProtectedRoute";
import DashboardLayout from "./components/DashboardLayout";
import FeatureGate from "./components/FeatureGate";
import Login from "./pages/Login";
import Signup from "./pages/Signup";
import OtpVerification from "./pages/OtpVerification";
import DashboardHome from "./pages/DashboardHome";
import DashboardEntry from "./pages/DashboardEntry";
import DashboardWorkspace from "./pages/DashboardWorkspace";
import DashboardSavedAnalyses from "./pages/DashboardSavedAnalyses";
import DashboardBilling from "./pages/DashboardBilling";
import DashboardSettings from "./pages/DashboardSettings";
import DashboardClients from "./pages/DashboardClients";
import DashboardProperties from "./pages/DashboardProperties";
import DashboardLayoutSetup from "./pages/DashboardLayoutSetup";
import DashboardDirections from "./pages/DashboardDirections";
import DashboardAnalysis from "./pages/DashboardAnalysis";
import DashboardRemedies from "./pages/DashboardRemedies";
import DashboardReports from "./pages/DashboardReports";
import DashboardPrescriptions from "./pages/DashboardPrescriptions";
import DashboardLearn from "./pages/DashboardLearn";
import DashboardHelp from "./pages/DashboardHelp";
import DashboardDevta from "./pages/DashboardDevta";
import DashboardAyadi from "./pages/DashboardAyadi";
import DashboardNumerology from "./pages/DashboardNumerology";
import DashboardAstroVastu from "./pages/DashboardAstroVastu";
import DashboardLalKitab from "./pages/DashboardLalKitab";
import DashboardInsights from "./pages/DashboardInsights";
import DashboardRecommendations from "./pages/DashboardRecommendations";
import AccountDisabled from "./pages/AccountDisabled";
import ProfileSetup from "./pages/ProfileSetup";
import AdminOverview from "./pages/admin/AdminOverview";
import AdminUsers from "./pages/admin/AdminUsers";
import AdminBilling from "./pages/admin/AdminBilling";
import AdminSystem from "./pages/admin/AdminSystem";
import AdminUserDetail from "./pages/admin/AdminUserDetail";
import AdminBillingDetail from "./pages/admin/AdminBillingDetail";
import AdminReportsStatus from "./pages/admin/AdminReportsStatus";
import AdminAnalysisErrors from "./pages/admin/AdminAnalysisErrors";
import AdminMasterData from "./pages/admin/AdminMasterData";
import AdminDebug from "./pages/admin/AdminDebug";

function ProtectedProviders({ children }) {
  return (
    <ClientsProvider>
      <PropertiesProvider>
        <DashboardProvider>{children}</DashboardProvider>
      </PropertiesProvider>
    </ClientsProvider>
  );
}

function AppRoutes() {
  const location = useLocation();

  useEffect(() => {
    trackPageView(location.pathname, { title: document.title });
  }, [location.pathname]);

  return (
    <Routes>
      <Route
        element={
          <div className="min-h-screen bg-slate-50 text-slate-900">
            <Header />
            <main className="pt-20">
              <Outlet />
            </main>
            <Footer />
          </div>
        }
      >
        <Route path="/" element={<Home />} />
        <Route path="/about" element={<About />} />
        <Route path="/features" element={<Features />} />
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/blog" element={<Blog />} />
        <Route path="/how-it-works" element={<HowItWorks />} />
        <Route path="/use-cases" element={<UseCases />} />
        <Route path="/faq" element={<FAQ />} />
        <Route path="/support" element={<Support />} />
        <Route path="/refund-policy" element={<RefundPolicy />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/terms" element={<TermsOfUse />} />
        <Route path="/disclaimer" element={<Disclaimer />} />
        <Route path="/login" element={<Login />} />
        <Route path="/signup" element={<Signup />} />
        <Route path="/otp-verification" element={<OtpVerification />} />
        <Route path="/account-disabled" element={<AccountDisabled />} />
      </Route>

      <Route
        path="/billing"
        element={
          <ProtectedRoute>
            <ProtectedProviders>
              <DashboardLayout>
                <FeatureGate featureKey="billing">
                  <DashboardBilling />
                </FeatureGate>
              </DashboardLayout>
            </ProtectedProviders>
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin"
        element={
          <ProtectedRoute requireAdmin>
            <Outlet />
          </ProtectedRoute>
        }
      >
        <Route index element={<Navigate to="/admin/overview" replace />} />
        <Route
          path="overview"
          element={
            <FeatureGate featureKey="analytics">
              <AdminOverview />
            </FeatureGate>
          }
        />
        <Route path="users" element={<AdminUsers />} />
        <Route path="users/:id" element={<AdminUserDetail />} />
        <Route path="billing" element={<Navigate to="/admin/billing/transactions" replace />} />
        <Route path="billing/transactions" element={<AdminBilling />} />
        <Route path="billing/transactions/:id" element={<AdminBillingDetail />} />
        <Route path="reports/status" element={<AdminReportsStatus />} />
        <Route path="analysis/errors" element={<AdminAnalysisErrors />} />
        <Route path="master-data" element={<AdminMasterData />} />
        <Route path="system" element={<AdminSystem />} />
        <Route path="debug" element={<AdminDebug />} />
      </Route>

      <Route
        path="/profile/setup"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <FeatureGate uiFeatureKey="profile">
                <ProfileSetup />
              </FeatureGate>
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <ProtectedProviders>
              <DashboardLayout>
                <Outlet />
              </DashboardLayout>
            </ProtectedProviders>
          </ProtectedRoute>
        }
      >
        <Route
          index
          element={
            <FeatureGate featureKey="dashboard">
              <DashboardEntry />
            </FeatureGate>
          }
        />
        <Route
          path="clients"
          element={
            <FeatureGate featureKey="clients">
              <DashboardClients />
            </FeatureGate>
          }
        />
        <Route
          path="properties"
          element={
            <FeatureGate featureKey="properties">
              <DashboardProperties />
            </FeatureGate>
          }
        />
        <Route
          path="layout"
          element={
            <FeatureGate featureKey="workspaceLayouts">
              <DashboardLayoutSetup />
            </FeatureGate>
          }
        />
        <Route
          path="directions"
          element={
            <FeatureGate featureKey="vastu.enabled">
              <DashboardDirections />
            </FeatureGate>
          }
        />
        <Route
          path="analysis"
          element={
            <FeatureGate featureKey="analysis">
              <DashboardAnalysis />
            </FeatureGate>
          }
        />
        <Route
          path="remedies"
          element={
            <FeatureGate uiFeatureKey="remedies">
              <DashboardRemedies />
            </FeatureGate>
          }
        />
        <Route
          path="reports"
          element={
            <FeatureGate featureKey="reports">
              <DashboardReports />
            </FeatureGate>
          }
        />
        <Route
          path="prescriptions"
          element={
            <FeatureGate uiFeatureKey="prescriptions">
              <DashboardPrescriptions />
            </FeatureGate>
          }
        />
        <Route
          path="learn"
          element={
            <FeatureGate uiFeatureKey="learn">
              <DashboardLearn />
            </FeatureGate>
          }
        />
        <Route
          path="help"
          element={
            <FeatureGate uiFeatureKey="help">
              <DashboardHelp />
            </FeatureGate>
          }
        />
        <Route
          path="insights"
          element={
            <FeatureGate uiFeatureKey="insights">
              <DashboardInsights />
            </FeatureGate>
          }
        />
        <Route
          path="recommendations"
          element={
            <FeatureGate uiFeatureKey="recommendations">
              <DashboardRecommendations />
            </FeatureGate>
          }
        />
        <Route
          path="workspace"
          element={
            <FeatureGate featureKey="workspaceLayouts">
              <DashboardWorkspace />
            </FeatureGate>
          }
        />
        <Route
          path="saved-analyses"
          element={
            <FeatureGate uiFeatureKey="saved-analyses">
              <DashboardSavedAnalyses />
            </FeatureGate>
          }
        />
        <Route
          path="billing"
          element={
            <FeatureGate featureKey="billing">
              <DashboardBilling />
            </FeatureGate>
          }
        />
        <Route
          path="settings"
          element={
            <FeatureGate featureKey="settings">
              <DashboardSettings />
            </FeatureGate>
          }
        />
        <Route
          path="devta"
          element={
            <FeatureGate featureKey="vastu.tools">
              <DashboardDevta />
            </FeatureGate>
          }
        />
        <Route
          path="ayadi"
          element={
            <FeatureGate featureKey="vastu.tools">
              <DashboardAyadi />
            </FeatureGate>
          }
        />
        <Route
          path="numerology"
          element={
            <FeatureGate featureKey="vastu.tools">
              <DashboardNumerology />
            </FeatureGate>
          }
        />
        <Route
          path="astro-vastu"
          element={
            <FeatureGate featureKey="vastu.tools">
              <DashboardAstroVastu />
            </FeatureGate>
          }
        />
        <Route
          path="lal-kitab"
          element={
            <FeatureGate featureKey="vastu.tools">
              <DashboardLalKitab />
            </FeatureGate>
          }
        />
      </Route>
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <PlatformContextProvider>
          <AppRoutes />
        </PlatformContextProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}
